import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import VideoPlayer from "../components/VideoPlayer";
import StreamViewer from "../components/StreamViewer";
import { QrOverlayLayer } from "../components/QrOverlayPanel";
import AgeGate from "../components/AgeGate";
import LiveBadge from "../components/LiveBadge";
import GiftsModal from "../components/GiftsModal";
import LiveShopRail from "../components/LiveShopRail";
import { Heart, Plus, Check, Share2, MessageSquare, Users, Radio, Gift, Pin, Clock, Lock, Crown, Camera } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { successHaptic, tapHaptic } from "../lib/haptics";
import PlayerSidebar from "../components/PlayerSidebar";
import UpNextRail from "../components/UpNextRail";
import LiveClipShareButton from "../components/LiveClipShareButton";
import ShareMenu from "../components/ShareMenu";
import { ReportButton } from "../components/ReportModal";
import OnStreamAlerts from "../components/OnStreamAlerts";
import LiveChatOverlay from "../components/LiveChatOverlay";
import WatchingWithYou from "../components/WatchingWithYou";
import { useMiniPlayer } from "../components/MiniPlayer";
import PpvPreviewLayer from "../components/PpvPreviewLayer";

export default function Watch() {
  const { videoId } = useParams();
  const { pathname, search } = useLocation();
  const isLiveRoute = pathname.startsWith("/live/");
  const { user } = useAuth();
  const nav = useNavigate();
  const [video, setVideo] = useState(null);
  const [live, setLive] = useState(null);
  const [related, setRelated] = useState([]);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [nextEp, setNextEp] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [comments, setComments] = useState({ pinned: [], recent: [] });
  const [commentText, setCommentText] = useState("");
  const [showTip, setShowTip] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [giftsOpen, setGiftsOpen] = useState(false);

  // Guest-invite prompt: when the URL has ?join=CODE, show a modal that asks
  // "you've been invited to appear as a guest — tap Accept to request a spot
  // in the stream". Accept → POST /api/live/{id}/guest/join which creates a
  // guest row the host can approve from their Guest Manager.
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteStatus, setInviteStatus] = useState(null); // null | "pending" | "waiting" | "declined" | "error"
  useEffect(() => {
    const p = new URLSearchParams(search);
    const code = p.get("join");
    if (code && isLiveRoute) setInviteCode(code);
  }, [search, isLiveRoute]);

  // Co-host pre-approved redeem — when URL has ?cohost_token=TOKEN, we skip
  // the approval queue entirely and drop the visitor straight on-air.
  const [cohostRedeeming, setCohostRedeeming] = useState(false);
  useEffect(() => {
    const p = new URLSearchParams(search);
    const token = p.get("cohost_token");
    if (!token || !isLiveRoute) return;
    if (!user) {
      // Preserve the token across the sign-in flow.
      const next = encodeURIComponent(`${pathname}${search}`);
      nav(`/login?next=${next}`, { replace: true });
      return;
    }
    (async () => {
      setCohostRedeeming(true);
      try {
        await api.post(`/live/${videoId}/cohost/redeem?token=${encodeURIComponent(token)}`);
        toast.success("You're on air as co-host", { id: "cohost-redeem" });
      } catch (e) {
        const code = e?.response?.status;
        if (code === 410) toast.error("This co-host link has expired", { id: "cohost-redeem" });
        else if (code === 404) toast.error("Invalid co-host link", { id: "cohost-redeem" });
        else toast.error("Could not join as co-host", { id: "cohost-redeem" });
      } finally {
        setCohostRedeeming(false);
      }
    })();
    // eslint-disable-next-line
  }, [search, isLiveRoute, user?.user_id, videoId]);

  // Live chat state
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  const [liveList, setLiveList] = useState([]);
  const wsRef = useRef(null);
  const [ppvAccess, setPpvAccess] = useState(null);
  const [ppvBusy, setPpvBusy] = useState(false);
  const { setActive: setMiniActive, setPosition: setMiniPos, getPosition: getMiniPos } = useMiniPlayer();

  // Mirror the currently-playing media into the global mini-player so it
  // surfaces as a sticky bar when the user navigates away to another route.
  useEffect(() => {
    const v = video || live;
    if (!v) return;
    setMiniActive({
      id: v.id,
      title: v.title,
      thumbnail: v.thumbnail_url,
      creator: v.creator_name,
      kind: isLiveRoute ? "live" : "vod",
    });
  }, [video, live, isLiveRoute, setMiniActive]);

  // Resume playback from `?t=<seconds>` (set when the user clicks the
  // sticky mini-player thumb), falling back to the mini-player's saved
  // position if they came back to the same VOD this session.
  const startAt = (() => {
    const tParam = parseInt(new URLSearchParams(search).get("t") || "", 10);
    if (Number.isFinite(tParam) && tParam > 0) return tParam;
    const saved = getMiniPos();
    return saved > 2 ? Math.floor(saved) : 0;
  })();

  useEffect(() => {
    setNotFound(false); setVideo(null); setLive(null); setNextEp(null); setCountdown(null);
    const onKidsRoute = window.location.pathname.startsWith("/kids/");
    const loadLive = async () => {
      try {
        const r = await api.get(`/live/${videoId}`);
        // Keep kids inside the kids section — if the stream is kid-safe
        // and we're not already on a /kids/* route, bounce to the
        // kid-branded live viewer.
        if (!onKidsRoute && (r.data?.is_kids_safe || r.data?.category === "kids" || r.data?.brand === "tap-in-kids")) {
          nav(`/kids/live/${videoId}`, { replace: true });
          return;
        }
        setLive(r.data);
      } catch { setNotFound(true); }
    };
    const loadVideo = async () => {
      try {
        const r = await api.get(`/videos/${videoId}`);
        // Same guard for VOD — keep kids in the kids section.
        if (!onKidsRoute && r.data?.is_kids_safe) {
          nav(`/kids/watch/${videoId}`, { replace: true });
          return;
        }
        setVideo(r.data);
        const rel = await api.get(`/videos?category=${r.data.category}&limit=12`);
        setRelated(rel.data.filter((v) => v.id !== r.data.id));
        if (r.data.series_slug) {
          try { const nx = await api.get(`/videos/${videoId}/next`); setNextEp(nx.data.next); } catch {}
        }
        try { const c = await api.get(`/videos/${videoId}/comments`); setComments(c.data); } catch {}
        if (r.data.is_ppv && user) {
          try { const a = await api.get(`/videos/${videoId}/ppv/access`); setPpvAccess(!!a.data.has_access); } catch { setPpvAccess(false); }
        }
      } catch { await loadLive(); }
    };
    if (isLiveRoute) loadLive(); else loadVideo();
    if (user && !isLiveRoute) {
      api.get("/watchlist").then((r) => {
        setInWatchlist((r.data || []).some((x) => x.id === videoId));
      }).catch(() => {});
    }
  }, [videoId, user, isLiveRoute, nav]);

  // Real-time live chat + viewer count via WebSocket
  useEffect(() => {
    if (!live?.id) return;
    // Load the list of other live streams for the "Also live right now" rail
    api.get("/live").then((r) => setLiveList(r.data || [])).catch(() => {});
    // Seed with history
    api.get(`/live/${live.id}/chat`).then((r) => setChat(r.data)).catch(() => {});

    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/, "ws");
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const url = `${wsBase}/api/ws/live/${live.id}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    let reconnectTimer = null;
    let closed = false;

    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === "viewer_count") setLiveViewerCount(data.viewer_count || 0);
            else if (data.type === "hello") setLiveViewerCount(data.viewer_count || 0);
            else if (data.type === "chat") setChat((prev) => [...prev.slice(-99), data]);
          } catch {}
        };
        ws.onclose = () => {
          if (closed) return;
          reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };
    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch {}
    };
  }, [live?.id]);

  const toggleWatchlist = async () => {
    if (!user) return toast.error("Please sign in");
    try {
      if (inWatchlist) {
        await api.delete(`/watchlist/${videoId}`);
        setInWatchlist(false);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/watchlist/${videoId}`);
        setInWatchlist(true);
        toast.success("Added to watchlist");
      }
    } catch { toast.error("Action failed"); }
  };

  const toggleLike = async () => {
    if (!user) return toast.error("Please sign in");
    try {
      const r = await api.post(`/likes/${videoId}`);
      toast.success(r.data.liked ? "Liked" : "Unliked");
    } catch { toast.error("Action failed"); }
  };

  // Next-episode auto-play countdown
  const onVideoEnded = () => {
    if (!nextEp) return;
    setCountdown(8);
  };
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) { nav(`/watch/${nextEp.id}`); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, nextEp, nav]);

  const postComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await api.post(`/videos/${videoId}/comments`, { text: commentText });
      setCommentText("");
      const c = await api.get(`/videos/${videoId}/comments`);
      setComments(c.data);
      successHaptic();
    } catch { toast.error("Sign in to comment"); }
  };
  const pinComment = async (c, pin) => {
    try { await api.post(`/comments/${c.id}/pin?pin=${pin}`); const r = await api.get(`/videos/${videoId}/comments`); setComments(r.data); }
    catch { toast.error("Only the creator can pin"); }
  };
  const sendTip = async (amount) => {
    if (!user) { toast.info("Sign in to tip"); return nav("/login"); }
    try {
      const r = await api.post("/tips", { target_id: video.creator_id, amount, message: "", origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch { toast.error("Tip failed"); }
  };

  const onProgress = (sec, dur) => {
    // Persist position in the mini-player context so cross-route resume works.
    if (sec) setMiniPos(sec);
    if (!user || !dur || !video) return;
    if (Math.floor(sec) % 5 === 0) {
      api.post(`/history/${videoId}?progress_sec=${Math.floor(sec)}&duration_sec=${Math.floor(dur)}`).catch(() => {});
    }
  };

  const sendChat = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to chat");
    if (!msg.trim() || !live) return;
    // Prefer WebSocket for instant delivery; fall back to REST if socket is closed.
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "chat", message: msg }));
    } else {
      try { await api.post(`/live/${live.id}/chat`, { message: msg }); } catch { return toast.error("Chat failed"); }
    }
    setMsg("");
  };

  if (notFound && !live && !video) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60">
        Content not found.
      </div>
    );
  }

  // ---------- LIVE layout — YouTube-style: header above, player below, suggestions right ----------
  if (live) {
    const suggestions = (liveList || []).filter((s) => s.id !== live.id).slice(0, 8);
    const isKidsStream = live.category === "kids" || live.is_kids_approved;
    // TV-embed mode: when iframed from the TV app (?embed=tv) we strip nav,
    // suggestions and chat sidebar so the player owns the entire viewport.
    const tvEmbed = new URLSearchParams(search).get("embed") === "tv";

    const acceptInvite = async () => {
      if (!user) { nav(`/signup?next=${encodeURIComponent(pathname + search)}`); return; }
      // Redirect into the dedicated waiting room — that page does the
      // /guest/join POST itself, opens the local camera + mic, and only
      // promotes the guest to /live/{id}?guest=1 once the host clicks
      // "Bring on-air" in their Backstage panel.
      nav(`/live/${live.id}/waiting-room?invite=${encodeURIComponent(inviteCode)}`);
    };

    if (tvEmbed) {
      return (
        <div data-testid="live-watch-tv-embed" className="fixed inset-0 bg-black">
          <StreamViewer streamId={live.id} kidsBrand={isKidsStream} />
          <OnStreamAlerts streamId={live.id} />
          <LiveChatOverlay streamId={live.id} messages={chat} isAuthed={!!user} />
          <div className="absolute top-3 left-3 z-30 pointer-events-none">
            <WatchingWithYou streamId={live.id} isAuthed={!!user} className="pointer-events-auto" />
          </div>
        </div>
      );
    }

    return (
      <div data-testid="live-watch-page" className="pt-28 px-4 sm:px-6 md:px-8 pb-20 max-w-[1400px] mx-auto">
        <div className="flex gap-6">
          <PlayerSidebar />
          <div className="flex-1 min-w-0">
        {/* Invite prompt — renders over the live page when ?join=CODE is on the URL. */}
        {inviteCode && inviteStatus !== "waiting" && (
          <div
            data-testid="invite-prompt-modal"
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <div className="bg-[#0B0F16] border border-tv-accent/30 rounded-2xl w-full max-w-md p-6 shadow-2xl">
              <div className="flex items-center gap-2 text-tv-accent uppercase tracking-[0.3em] text-xs font-black mb-2">
                <Radio className="w-4 h-4" /> You're invited
              </div>
              <h2 className="font-display font-black text-2xl text-white leading-tight">
                {live.creator_name || "A host"} wants you as a guest on<br />
                <span className="text-tv-accent">"{live.title}"</span>
              </h2>
              <p className="text-white/60 text-sm mt-3">
                Tap Accept to request a spot in the stream. The host will admit you when they're ready. You'll need your camera + mic ready.
              </p>
              <div className="flex items-center gap-2 mt-5">
                <button
                  data-testid="invite-accept-btn"
                  disabled={inviteStatus === "pending"}
                  onClick={acceptInvite}
                  className="flex-1 bg-tv-live hover:bg-red-600 text-white font-bold px-5 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Radio className="w-4 h-4" /> {inviteStatus === "pending" ? "Sending…" : "Accept invite"}
                </button>
                <button
                  data-testid="invite-decline-btn"
                  onClick={() => { setInviteCode(null); setInviteStatus("declined"); }}
                  className="bg-white/5 hover:bg-white/10 text-white font-semibold px-4 py-3 rounded-full text-sm"
                >
                  Not now
                </button>
              </div>
              <div className="text-[10px] text-white/40 italic mt-3 text-center">You can still watch as a viewer if you decline.</div>
            </div>
          </div>
        )}

        {inviteStatus === "waiting" && (
          <div
            data-testid="invite-waiting-banner"
            className="mb-4 bg-emerald-500/15 border border-emerald-500/40 rounded-xl p-3 text-center text-emerald-200 text-sm"
          >
            Waiting for host to admit you — they can see you in their Backstage panel.
          </div>
        )}

        <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
          <div className="min-w-0 max-w-[1000px] w-full mx-auto lg:mx-0">

            {/* TOP HEADER — live status + title + host banner, BEFORE the player */}
            <header className="mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <LiveBadge />
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">{(live.category || "").replace(/_/g, " ")}</span>
                <div data-testid="live-viewer-count" className="rounded-full px-3 py-1 text-xs text-white flex items-center gap-2 bg-white/5 border border-white/10">
                  <Users className="w-3 h-3" /> {(liveViewerCount || live.viewer_count || 0).toLocaleString()} watching
                </div>
              </div>
              <h1 data-testid="live-title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-white tracking-tight leading-tight mt-3">
                {live.title}
              </h1>

              {user && (
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  <SnapHighlightButton streamId={live.id} creatorName={live.creator_name} />
                  <LiveClipShareButton
                    streamId={live.id}
                    streamTitle={live.title}
                    creatorName={live.creator_name}
                    seconds={30}
                  />
                </div>
              )}

              {/* Host banner / profile — directly beneath the title */}
              <Link
                to={`/creator/${live.creator_id}`}
                data-testid="live-host-banner"
                className="mt-3 inline-flex items-center gap-3 hover:bg-white/5 rounded-xl p-2 pr-5 transition"
              >
                <img src={live.creator_avatar} className="w-12 h-12 rounded-full object-cover ring-2 ring-white/15" alt="" />
                <div>
                  <div className="text-white font-semibold text-sm">{live.creator_name}</div>
                  <div className="text-[11px] text-white/50 uppercase tracking-wider font-bold">Visit channel →</div>
                </div>
              </Link>
            </header>

            {/* PLAYER — slightly smaller container so the header breathes above it */}
            <div className="max-w-[960px] relative">
              <StreamViewer streamId={live.id} kidsBrand={isKidsStream} />
              {/* Host's QR overlays — payment / ad / affiliate / link QRs the
                  host pinned via DirectorsMode → QrOverlayPanel. Honors the
                  host's master Visible/Hidden toggle. */}
              <QrOverlayLayer overlays={live.overlays} />
              {/* On-stream follower/tip alerts — animated banners that pop
                  the moment someone follows or tips during the stream. */}
              <OnStreamAlerts streamId={live.id} />
              {/* iter211 — TikTok-Live style floating chat overlay +
                  long-press emoji reactions. Renders inside the player
                  container so it doesn't compete with the sidebar chat
                  list on desktop, while still floating on mobile. */}
              <LiveChatOverlay streamId={live.id} messages={chat} isAuthed={!!user} />
              {/* Watching-with-you avatar strip — social proof for viewers. */}
              <div className="absolute top-3 left-3 z-30">
                <WatchingWithYou streamId={live.id} isAuthed={!!user} />
              </div>
            </div>

            {live.description && (
              <p className="mt-4 text-white/80 text-sm leading-relaxed whitespace-pre-wrap border-t border-white/5 pt-4">
                {live.description}
              </p>
            )}
            <LiveShopRail streamId={live.id} />
          </div>

          {/* RIGHT: Suggested live streams */}
          <aside data-testid="live-suggestions" className="min-w-0">
            <div className="flex items-center gap-2 text-white font-display font-bold text-sm mb-3">
              <Radio className="w-4 h-4 text-tv-live" /> Also live right now
              {suggestions.length > 0 && <span className="text-white/40 text-xs font-normal">· {suggestions.length}</span>}
            </div>
            {suggestions.length === 0 ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-white/50 text-xs" data-testid="live-suggestions-empty">
                No other streams on air. Check <Link to="/live" className="text-tv-accent underline">upcoming streams</Link>.
              </div>
            ) : (
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <Link
                    key={s.id}
                    to={`/live/${s.id}`}
                    data-testid={`live-suggestion-${s.id}`}
                    className="flex gap-3 p-2 rounded-xl hover:bg-white/5 transition group"
                  >
                    <div className="relative w-32 aspect-video rounded-lg overflow-hidden bg-black shrink-0">
                      <img src={s.thumbnail_url || "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=400&q=80"} alt="" className="w-full h-full object-cover opacity-95 group-hover:opacity-100" />
                      <span className="absolute top-1 left-1 bg-tv-live text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> Live
                      </span>
                      {s.viewer_count > 0 && (
                        <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">{s.viewer_count}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-xs font-bold line-clamp-2 leading-snug">{s.title}</div>
                      <div className="text-white/50 text-[11px] mt-1 truncate">{s.creator_name || "Creator"}</div>
                      <div className="text-white/30 text-[10px] mt-0.5 uppercase tracking-wider">{(s.category || "live").replace(/_/g, " ")}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </aside>
        </div>

        {/* Brand tagline strip — also at the bottom of LIVE pages */}
        <section data-testid="watch-tagline-live" className="mt-12 pt-8 border-t border-white/10 text-center">
          <div className="font-display font-black text-base sm:text-lg tracking-[0.3em] uppercase bg-gradient-to-r from-red-400 via-rose-400 to-amber-400 bg-clip-text text-transparent">
            Real Stories. Original Voices. Limitless Possibilities.
          </div>
          <div className="text-white/40 text-[11px] uppercase tracking-[0.4em] mt-2">Tap IN TV Network</div>
        </section>
          </div>
        </div>
      </div>
    );
  }

  // ---------- VOD layout ----------
  if (!video) return <div className="min-h-screen" />;

  return (
    <div data-testid="watch-page" className="pt-32 px-4 sm:px-8 md:px-12 pb-20 max-w-[1500px] mx-auto">
      <div className="flex gap-6">
        <PlayerSidebar />
        <div className="flex-1 min-w-0">
      <GiftsModal open={giftsOpen} onClose={() => setGiftsOpen(false)} creator={{ user_id: video.creator_id, name: video.creator_name }} />
      <AgeGate rating={video.rating}>
        <div className="relative">
          {video.is_ppv && ppvAccess === false && !user ? (
            <div data-testid="ppv-gate" className="relative aspect-video rounded-2xl overflow-hidden tv-glass flex items-center justify-center">
              {video.thumbnail_url && <img src={video.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
              <div className="relative z-10 text-center p-6">
                <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Pay-per-view</div>
                <h2 className="font-display font-black text-4xl md:text-5xl text-white mt-2 tracking-tight">Sign in to preview</h2>
                <p className="text-white/70 mt-2 max-w-md mx-auto">Free preview is for signed-in viewers only. After {video.ppv_preview_seconds || 30}s you'll be asked to unlock for ${video.ppv_price_usd?.toFixed(2)}.</p>
                <button
                  data-testid="ppv-signin-btn"
                  onClick={() => nav("/login")}
                  className="mt-6 bg-tv-accent hover:bg-yellow-400 text-black font-bold px-8 py-3 rounded-full"
                >
                  Sign in to start preview
                </button>
              </div>
            </div>
          ) : video.is_ppv && ppvAccess === false ? (
            <PpvPreviewLayer
              video={video}
              startAt={startAt}
              onProgress={onProgress}
              onEnded={onVideoEnded}
              onUnlocked={() => setPpvAccess(true)}
            />
          ) : live ? (
            <div className="relative">
              <StreamViewer streamId={live.id} kidsBrand={live.category === "kids"} />
              <QrOverlayLayer overlays={live.overlays} />
              <LiveChatOverlay streamId={live.id} messages={chat} isAuthed={!!user} />
              <div className="absolute top-3 left-3 z-30">
                <WatchingWithYou streamId={live.id} isAuthed={!!user} />
              </div>
            </div>
          ) : video.access_locked ? (
            <div data-testid="members-only-gate" className="relative aspect-video rounded-2xl overflow-hidden tv-glass flex items-center justify-center">
              {video.thumbnail_url && <img src={video.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              <div className="relative z-10 text-center p-6 max-w-lg">
                <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center justify-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Members-only
                </div>
                <h2 className="font-display font-black text-4xl md:text-5xl text-white mt-2 tracking-tight">Join {video.creator_name}'s Fan Club to watch</h2>
                <p className="text-white/70 mt-3 text-sm">This episode is reserved for monthly members. Subscribe and watch every members-only drop, live Q&As, and bonus behind-the-scenes — cancel anytime.</p>
                <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                  <button
                    data-testid="members-only-join-btn"
                    onClick={() => nav(`/creator/${video.creator_id}#fanclub`)}
                    className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" /> Join Fan Club
                  </button>
                  <Link to={`/creator/${video.creator_id}`} className="text-white/70 hover:text-white font-semibold px-4 py-3 text-sm">
                    Visit channel →
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <VideoPlayer videoId={video.id} src={video.video_url} poster={video.thumbnail_url} startAt={startAt} onProgress={onProgress} onEnded={onVideoEnded} vtt={video.vtt_url || video.captions_vtt_url} embedUrl={video.is_external && video.transcode_status !== "ready" ? video.embed_url : undefined} sourceType={video.is_external ? video.source_type : undefined} watermark={(live || video.is_kids_approved || video.rating === "G" || video.category === "kids") ? ((video.is_kids_approved || video.category === "kids") ? "kids" : "main") : undefined} />
          )}
          {countdown !== null && nextEp && (
            <div data-testid="next-ep-countdown" className="absolute bottom-4 right-4 tv-glass rounded-xl p-4 min-w-[260px] border border-white/20">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-tv-accent font-bold mb-2"><Clock className="w-4 h-4" /> Next episode · {countdown}s</div>
              <div className="text-white font-display font-bold leading-tight line-clamp-2">{nextEp.title}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => nav(`/watch/${nextEp.id}`)} className="bg-tv-accent text-black font-bold px-3 py-1.5 rounded-full text-xs">Play now</button>
                <button onClick={() => setCountdown(null)} className="tv-glass text-white px-3 py-1.5 rounded-full text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </AgeGate>

      <div className="mt-8 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.25em] text-tv-accent font-semibold">{video.category_name || video.category}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white mt-2 tracking-tighter">{video.title}</h1>
          <div className="flex items-center gap-4 mt-4 text-sm text-white/70">
            <span>{video.view_count?.toLocaleString()} views</span>
            <span>•</span>
            <span>{video.like_count?.toLocaleString()} likes</span>
            {video.is_premium && <span className="bg-tv-accent text-black px-2 py-0.5 rounded text-[10px] font-bold uppercase">Premium</span>}
            {video.is_ppv && <span className="bg-purple-500 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase">PPV ${video.ppv_price_usd?.toFixed(2)}</span>}
          </div>
          <p className="mt-6 text-white/80 leading-relaxed">{video.description}</p>

          {/* Replay tip prompt — only shown for live recordings so fans who missed the broadcast can still show love */}
          {(video.source_type === "live_recording" || video.source_type === "live_replay" || (Array.isArray(video.tags) && (video.tags.includes("live-replay") || video.tags.includes("recording")))) && (
            <div
              data-testid="replay-tip-banner"
              className="mt-6 relative overflow-hidden rounded-2xl border border-tv-accent/30 bg-gradient-to-br from-tv-accent/15 via-red-500/10 to-purple-500/15 p-5"
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-1.5">
                    <Radio className="w-3 h-3" /> Replay of a live broadcast
                  </div>
                  <h3 className="font-display font-black text-xl text-white mt-1 tracking-tight">
                    Missed it live? Send {video.creator_name} some love.
                  </h3>
                  <p className="text-white/70 text-sm mt-1">
                    Tips go straight to the creator (minus platform fee). Every dollar keeps indie on air.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[5, 10, 25].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => sendTip(amt)}
                      data-testid={`replay-tip-${amt}`}
                      className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-full text-sm shadow-lg shadow-tv-accent/20"
                    >
                      Tip ${amt}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowTip(true)}
                    data-testid="replay-tip-custom"
                    className="bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2 rounded-full text-sm"
                  >
                    Custom
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6 flex-wrap">
            <button data-testid="watch-like-btn" onClick={() => { tapHaptic(); toggleLike(); }} className="tv-glass hover:bg-white/10 px-4 py-2 rounded-full text-white flex items-center gap-2">
              <Heart className="w-4 h-4" /> Like
            </button>
            <button data-testid="watch-watchlist-btn" onClick={toggleWatchlist} className="tv-glass hover:bg-white/10 px-4 py-2 rounded-full text-white flex items-center gap-2">
              {inWatchlist ? <Check className="w-4 h-4 text-tv-brand" /> : <Plus className="w-4 h-4" />}
              {inWatchlist ? "In Watchlist" : "Add to Watchlist"}
            </button>
            <button data-testid="watch-share-btn" onClick={() => setShareOpen((v) => !v)} className="tv-glass hover:bg-white/10 px-4 py-2 rounded-full text-white flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Share
            </button>
            {video?.id && (
              <ReportButton
                targetType={video.is_live ? "stream" : "video"}
                targetId={video.id}
                label="Report"
                className="tv-glass hover:bg-red-500/10 hover:text-red-400 px-4 py-2 rounded-full text-white/70 flex items-center gap-2 text-sm"
              />
            )}
            <button data-testid="watch-tip-btn" onClick={() => setShowTip(!showTip)} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-full flex items-center gap-2">
              <Gift className="w-4 h-4" /> Send a Tip
            </button>
            <button data-testid="watch-gift-btn" onClick={() => setGiftsOpen(true)} className="bg-gradient-to-r from-pink-500 to-purple-500 hover:brightness-110 text-white font-bold px-4 py-2 rounded-full flex items-center gap-2">
              <Gift className="w-4 h-4" /> Send Gift
            </button>
          </div>

          {shareOpen && (
            <div data-testid="watch-share-panel" className="mt-4 tv-glass rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 font-black mb-2">Share this to…</div>
              <ShareMenu
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={video?.title ? `${video.title} — Tap IN TV` : "Tap IN TV Network"}
                text={video?.title ? `Watch "${video.title}" on Tap IN` : "Check this out on Tap IN"}
                testidPrefix="watch-share"
              />
            </div>
          )}

          {showTip && (
            <div data-testid="tip-panel" className="mt-4 tv-glass rounded-xl p-5">
              <div className="text-sm text-white/80 mb-3">Show some love to {video.creator_name}:</div>
              <div className="flex gap-2 flex-wrap">
                {[1, 5, 10, 25, 50].map((amt) => (
                  <button key={amt} onClick={() => sendTip(amt)} data-testid={`tip-${amt}`} className="bg-tv-brand/20 hover:bg-tv-brand/30 text-tv-brand font-bold px-4 py-2 rounded-full text-sm">${amt}</button>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <section className="mt-10">
            <h2 className="font-display font-bold text-2xl text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Comments
            </h2>
            {user ? (
              <form onSubmit={postComment} className="flex gap-2 mb-5">
                <input
                  data-testid="comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-white outline-none focus:border-tv-brand"
                />
                <button data-testid="comment-submit" className="bg-tv-accent text-black font-bold px-5 rounded-full">Post</button>
              </form>
            ) : (
              <div className="text-white/50 text-sm mb-5">Sign in to comment.</div>
            )}
            {[...comments.pinned, ...comments.recent].length === 0 ? (
              <div className="text-white/50 text-sm">Be the first to leave a comment.</div>
            ) : (
              <div className="space-y-3">
                {comments.pinned.map((c) => <CommentItem key={c.id} c={c} isOwner={user?.user_id === video.creator_id} pinComment={pinComment} pinned />)}
                {comments.recent.map((c) => <CommentItem key={c.id} c={c} isOwner={user?.user_id === video.creator_id} pinComment={pinComment} />)}
              </div>
            )}
          </section>
        </div>

        <aside className="tv-glass rounded-xl p-5 h-fit">
          <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-3">Creator</div>
          <Link to={`/creator/${video.creator_id}`} className="flex items-center gap-3 group" data-testid="watch-creator-link">
            <img src={video.creator_avatar} alt="" className="w-14 h-14 rounded-full object-cover border border-white/10" />
            <div>
              <div className="font-display font-bold text-lg text-white group-hover:text-tv-brand">{video.creator_name}</div>
              <div className="text-xs text-white/60">Visit channel →</div>
            </div>
          </Link>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display font-bold text-2xl text-white mb-4">More in {video.category_name || video.category}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {related.slice(0, 8).map((r) => (
              <Link key={r.id} to={`/watch/${r.id}`} className="tv-card rounded-xl overflow-hidden group" data-testid={`related-${r.id}`}>
                <div className="aspect-video bg-black">
                  <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-white font-semibold text-sm line-clamp-1">{r.title}</div>
                  <div className="text-xs text-white/50">{r.creator_name}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Brand tagline strip — pinned at bottom of every Watch page */}
      <section data-testid="watch-tagline" className="mt-16 pt-8 border-t border-white/10 text-center">
        <div className="font-display font-black text-base sm:text-lg tracking-[0.3em] uppercase bg-gradient-to-r from-red-400 via-rose-400 to-amber-400 bg-clip-text text-transparent">
          Real Stories. Original Voices. Limitless Possibilities.
        </div>
        <div className="text-white/40 text-[11px] uppercase tracking-[0.4em] mt-2">Tap IN TV Network</div>
      </section>
        </div>
        <UpNextRail items={related} currentId={video.id} />
      </div>
    </div>
  );
}

function CommentItem({ c, isOwner, pinComment, pinned }) {
  return (
    <div data-testid={`comment-${c.id}`} className={`flex gap-3 ${pinned ? "tv-glass rounded-xl p-3 border border-tv-accent/30" : ""}`}>
      <div className="w-9 h-9 rounded-full bg-tv-brand text-black font-bold flex items-center justify-center shrink-0">
        {c.user_name?.[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-white font-semibold text-sm">{c.user_name}</span>
          {c.user_verified && <span className="text-tv-brand text-xs">✓</span>}
          {pinned && <span className="text-[10px] uppercase tracking-wider text-tv-accent font-bold">Pinned</span>}
          <span className="text-xs text-white/40">{new Date(c.created_at).toLocaleDateString()}</span>
        </div>
        <div className="text-sm text-white/85 mt-0.5 whitespace-pre-wrap">{c.text}</div>
      </div>
      {isOwner && (
        <button onClick={() => pinComment(c, !c.is_pinned)} className="text-white/50 hover:text-tv-accent" title={c.is_pinned ? "Unpin" : "Pin"}>
          <Pin className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}


function SnapHighlightButton({ streamId, creatorName }) {
  const [busy, setBusy] = useState(false);
  const snap = async () => {
    setBusy(true);
    try {
      await api.post(`/community/stories/${streamId}/snippet`, {
        title: `📸 Snippet from ${creatorName || "live"}`,
      });
      toast.success("Dropped to your Story Bar");
    } catch (e) { toast.error(e?.response?.data?.detail || "Snap failed"); }
    finally { setBusy(false); }
  };
  return (
    <button
      data-testid="live-snap-highlight-btn"
      onClick={snap}
      disabled={busy}
      className="mt-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-tv-accent hover:text-black text-white font-bold text-xs px-3 py-1.5 rounded-full transition"
      title="Drop a snippet of this moment to your story bar"
    >
      <Camera className="w-3.5 h-3.5" /> {busy ? "Snapping…" : "Snap this moment"}
    </button>
  );
}

